<template>
  <div class="field-container">
    <label :for="id" class="field-label">
      {{ $t(`${field}.title`) }}
    </label>

    <div class="field-description" v-if="hasDescription">
      {{ $t(`${field}.description`) }}
    </div>

    <slot :id :invalid="validation.invalid" :touch="validation.touch" />

    <div v-if="validation.error" class="invalid-feedback">
      {{ validation.error }}
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ValidationRuleCollection } from "@vuelidate/core";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { GeneratorReviewKey } from "./fieldKeys";
import { useFieldValidation } from "./useFieldValidation";

/**
 * Label, description and validation message around one signup form input. The input goes in the
 * default slot, which receives `id`, `invalid` and `touch` for its styling and blur. Without a slot it
 * renders an explanation row: the label and description only.
 */
const props = withDefaults(
  defineProps<{
    field: GeneratorReviewKey;
    value?: unknown;
    rules?: ValidationRuleCollection;
  }>(),
  { value: undefined, rules: () => ({}) }
);

const { te } = useI18n();
const hasDescription = computed(() => te(`${props.field}.description`));
// The entry's own name, e.g. `device_name`.
const id = computed(() => props.field.slice(props.field.lastIndexOf(".") + 1));

const validation = useFieldValidation(
  () => props.value,
  () => props.rules
);
</script>

<style scoped lang="scss">
.field-container {
  margin-bottom: 1rem;
}

.field-label {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.field-description {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}
</style>
